/**
 * Plain-data SEO/AEO source of truth. Kept free of JSX and React imports so
 * the build-time prerender script (scripts/prerender.js) can require it in
 * plain Node and emit the exact same titles, descriptions and schema as the
 * React app renders at runtime.
 */

export const SITE = "https://endokrinoloog.ee";
export const CLINIC_ID = `${SITE}/#clinic`;
export const DOCTOR_ID = `${SITE}/#verner-fogel`;

export const CLINIC = {
  name: "Tallinna Endokrinoloogia Kliinik",
  logo: `${SITE}/icon-512.png`,
  image: `${SITE}/hero-desktop.webp`,
  telephone: ["+37255665451", "+37255665135"],
  email: "info@endokrinoloog.ee",
  priceRange: "5–140 €",
  languages: ["et", "ru", "en"],
  bookingUrl: "https://veebiregistratuur.ee/#/tallinna-endokrinoloogia-kliinik/",
  // Only Tallinn has a verified coordinate pair (same value as the geo meta
  // tags that have been on the site since launch). Pärnu links to a map
  // search instead of an invented coordinate.
  addresses: [
    {
      streetAddress: "Tartu mnt 16, 3. korrus",
      addressLocality: "Tallinn",
      postalCode: "10117",
      geo: { latitude: 59.437, longitude: 24.7536 },
      map: "https://www.google.com/maps/search/?api=1&query=Tartu+mnt+16,+Tallinn",
    },
    {
      streetAddress: "Metsa 28",
      addressLocality: "Pärnu",
      postalCode: "80010",
      map: "https://www.google.com/maps/search/?api=1&query=Metsa+28,+P%C3%A4rnu",
    },
  ],
  openingHours: [
    { days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "10:00", closes: "18:00" },
    { days: ["Saturday"], opens: "09:00", closes: "17:00" },
  ],
  // IDN domains must be punycode inside JSON-LD to stay machine-readable.
  sameAs: ["https://www.xn--kilpnrmekliinik-4kba.ee", "https://www.ajagraafik.ee"],
};

export const REVIEWER = {
  name: "Dr. Verner Fogel",
  jobTitle: { et: "Endokrinoloog", ru: "Эндокринолог", en: "Endocrinologist" },
  image: `${SITE}/doctors/verner-fogel.webp`,
};

export const PAGE_TITLES = {
  home: {
    et: "Tallinna Endokrinoloogia Kliinik — endokrinoloog Tallinnas ja Pärnus",
    ru: "Таллиннская клиника эндокринологии — эндокринолог в Таллинне и Пярну",
    en: "Tallinn Endocrinology Clinic — endocrinologist in Tallinn and Pärnu",
  },
  services: {
    et: "Teenused — Tallinna Endokrinoloogia Kliinik",
    ru: "Услуги — Таллиннская клиника эндокринологии",
    en: "Services — Tallinn Endocrinology Clinic",
  },
  pricing: {
    et: "Hinnakiri — Tallinna Endokrinoloogia Kliinik",
    ru: "Цены — Таллиннская клиника эндокринологии",
    en: "Pricing — Tallinn Endocrinology Clinic",
  },
  doctors: {
    et: "Arstid ja õed — Tallinna Endokrinoloogia Kliinik",
    ru: "Врачи и медсёстры — Таллиннская клиника эндокринологии",
    en: "Doctors and nurses — Tallinn Endocrinology Clinic",
  },
  contact: {
    et: "Kontakt ja sagedased küsimused — Tallinna Endokrinoloogia Kliinik",
    ru: "Контакты и частые вопросы — Таллиннская клиника эндокринологии",
    en: "Contact and common questions — Tallinn Endocrinology Clinic",
  },
  diseases: {
    et: "Haigused — Tallinna Endokrinoloogia Kliinik",
    ru: "Заболевания — Таллиннская клиника эндокринологии",
    en: "Diseases — Tallinn Endocrinology Clinic",
  },
  calculators: {
    et: "Tervisekalkulaatorid — HbA1c, KMI, ICR ja ISF | Tallinna Endokrinoloogia Kliinik",
    ru: "Калькуляторы здоровья — HbA1c, ИМТ, ICR и ISF | Таллиннская клиника эндокринологии",
    en: "Health calculators — HbA1c, BMI, ICR and ISF | Tallinn Endocrinology Clinic",
  },
};

export const PAGE_DESCRIPTIONS = {
  home: {
    et: "Endokrinoloog, diabeedi- ja jalaõde Tallinnas (Tartu mnt 16) ja Pärnus (Metsa 28). Kilpnäärme uuringud, diabeedi nõustamine, ravipediküür. Broneeri aeg.",
    ru: "Эндокринолог, медсёстры по диабету и стопе в Таллинне (Тарту мнт 16) и Пярну (Метса 28). УЗИ щитовидной железы, консультации диабета, лечебный педикюр.",
    en: "Endocrinologist, diabetes & foot nurses in Tallinn (Tartu mnt 16) and Pärnu (Metsa 28). Thyroid investigations, diabetes counselling, medical pedicure.",
  },
  services: {
    et: "Endokriinhaiguste diagnostika ja ravi: kilpnääre, diabeet, hormoonid. Vaata kõiki teenuseid Tallinnas ja Pärnus.",
    ru: "Диагностика и лечение эндокринных заболеваний: щитовидная железа, диабет, гормоны.",
    en: "Diagnosis and treatment of endocrine diseases: thyroid, diabetes, hormones.",
  },
  pricing: {
    et: "Tasulised endokrinoloogi, diabeediõe ja jalaõe teenused. Vastuvõtt 20€ Tervisekassaga, alates 40€ ilma saatekirjata.",
    ru: "Платные услуги эндокринолога, медсестёр по диабету и стопе. Приём от 20€ с медстраховкой, от 40€ без направления.",
    en: "Paid services of endocrinologist, diabetes & foot nurses. Appointment from 20€ with insurance, from 40€ without referral.",
  },
  doctors: {
    et: "Dr Verner Fogel, õde Liina Sildnik, õde Riina Vello, õde Kersti Saar.",
    ru: "Доктор Вернер Фогель, медсёстры Лийна Сильдник, Рийна Велло, Керсти Саар.",
    en: "Dr Verner Fogel, nurse Liina Sildnik, nurse Riina Vello, nurse Kersti Saar.",
  },
  contact: {
    et: "Telefon +372 5566 5451, +372 5566 5135. Tallinn Tartu mnt 16, Pärnu Metsa 28. Vastused sagedastele küsimustele: saatekiri, hinnad, tühistamine, vanusepiir.",
    ru: "Телефон +372 5566 5451, +372 5566 5135. Таллинн Тарту мнт 16, Пярну Метса 28. Ответы на частые вопросы: направление, цены, отмена, возраст.",
    en: "Phone +372 5566 5451, +372 5566 5135. Tallinn Tartu mnt 16, Pärnu Metsa 28. Answers to common questions: referral, prices, cancellation, age limit.",
  },
  diseases: {
    et: "Loe artikleid kilpnäärme haiguste kohta: Hashimoto, Graves, hüpotüreoidism, hüpertüreoidism, kilpnäärme sõlmed, kilpnäärmevähk, rasedus ja kilpnääre.",
    ru: "Читайте статьи о заболеваниях щитовидной железы: Хашимото, Грейвс, гипотиреоз, гипертиреоз, узлы щитовидной железы, рак щитовидной железы, беременность и щитовидная железа.",
    en: "Read articles about thyroid diseases: Hashimoto, Graves, hypothyroidism, hyperthyroidism, thyroid nodules, thyroid cancer, pregnancy and thyroid.",
  },
  calculators: {
    et: "Tasuta tervisekalkulaatorid: HbA1c teisendus, kehamassiindeks (KMI) ja vööümbermõõdu risk, ICR ja ISF.",
    ru: "Бесплатные калькуляторы: HbA1c, ИМТ, талия, ICR и ISF.",
    en: "Free health calculators: HbA1c conversion, BMI and waist risk, ICR and ISF.",
  },
};

export const BREADCRUMB_HOME = { et: "Avaleht", ru: "Главная", en: "Home" };
